import React, { useEffect, useState } from 'react';
import { getSecureItem } from '@/utils/localStorage';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { IndianRupee } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { AddJobDialog } from '@/components/hiring/AddJobDialog';
import {
  Search,
  Filter,
  Calendar,
  Building2,
  Eye,
  X,
  Briefcase,
  MapPin,
  Edit,
  Trash2,
  AlertTriangle,
  FileText,
  Activity,
  User,
  Info,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  JobPosting as JobPostingBase,
  getJobPostings,
  createJobPosting,
  updateJobPosting,
  updateJobStatus,
  deleteJobPosting,
  JOB_TYPES,
  JOB_STATUSES,
} from '@/services/job.service';
import { candidateService } from '@/services/candidate.service';
import JobTimelineDialog from '@/components/hiring/JobTimelineDialog';
import { TimelineEntry, JobStatus } from '@/types/models';
import { useAuthStore } from '@/stores/authStore';
import { useSharedData } from '@/hooks/useSharedData';
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";

type JobPosting = JobPostingBase & { allowedStatuses?: any[] };

interface JobsListProps {
  limit?: number;
  showFilters?: boolean;
}

const JobsList: React.FC<JobsListProps> = ({ limit, showFilters = true }) => {
  const navigate = useNavigate();
  // const { toast } = useToast(); - Removed as we use sonner directly
  const { hasSubModulePermission, isLoading } = useAuthStore();
  const { departments, jobs: sharedJobs, candidates: sharedCandidates } = useSharedData();

  const fetchJobs = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getJobPostings();
      const activeJobs = (response.data || []).filter((job: any) => job.isActive !== false);
      setJobs(activeJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      // Handled globally
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Listen for status updates from HiringSettings
  React.useEffect(() => {
    const handleStatusUpdate = (event: CustomEvent) => {
      const { category } = event.detail;

      // Only refresh if the status is for jobPosting category
      if (category === 'jobPosting') {
        fetchJobs();
      }
    };

    window.addEventListener('status-updated', handleStatusUpdate as EventListener);

    return () => {
      window.removeEventListener('status-updated', handleStatusUpdate as EventListener);
    };
  }, [fetchJobs]);

  // Check authentication
  React.useEffect(() => {
    // Only check authentication if not loading
    if (!isLoading) {
      const token = getSecureItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }
    }
  }, [navigate, isLoading]);

  // Check if user has basic job view permissions
  const canPostJobs = hasSubModulePermission('hiring', 'jobPosting', 'create');
  const canEditJobs = hasSubModulePermission('hiring', 'jobPosting', 'edit');
  const canDeleteJobs = hasSubModulePermission('hiring', 'jobPosting', 'delete');

  // Permission helper function


  const [searchQuery, setSearchQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [departmentFilter, setDepartmentFilter] = React.useState('all');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [locationFilter, setLocationFilter] = React.useState('all');
  const [dateRangeFilter, setDateRangeFilter] = React.useState('all');
  const [sortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'postedDate', direction: 'desc' });
  const [selectedTimelineJob, setSelectedTimelineJob] = React.useState<JobPosting | null>(null);
  const [selectedJob, setSelectedJob] = React.useState<JobPosting | null>(null);
  const [isProfileDirty, setIsProfileDirty] = React.useState(false);
  const [pendingStatus, setPendingStatus] = React.useState<string | null>(null);
  const [jobs, setJobs] = React.useState<JobPosting[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [jobToEdit, setJobToEdit] = React.useState<JobPosting | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [isTimelineDialogOpen, setIsTimelineDialogOpen] = React.useState(false);
  const [selectedTimelineEventId, setSelectedTimelineEventId] = React.useState<string | null>(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [applicantCounts, setApplicantCounts] = useState<Record<string, number>>({});
  const [singleEventOnly, setSingleEventOnly] = React.useState(false);
  const hasInitialized = React.useRef(false);

  // Confirmation dialog state - similar to candidates page
  const [confirmDialog, setConfirmDialog] = React.useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'warning' | 'info';
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => { },
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'info'
  });

  // Helper to safely get job ID regardless of field name returned by API
  const getJobId = (job: Partial<JobPosting> | any): string => {
    return (job?._id || job?.id || '') as string;
  };



  const fetchCandidates = React.useCallback(async () => {
    try {
      const response = await candidateService.getCandidates({});
      setCandidates(response.data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  }, []);

  // Use shared data if available, otherwise fetch locally
  React.useEffect(() => {
    if (hasInitialized.current) return;

    // Use shared jobs data if available
    if (sharedJobs.length > 0) {
      setJobs(sharedJobs.filter((job: any) => job.isActive !== false));
      setLoading(false); // Make sure to turn off loading when we use shared data
    } else {
      fetchJobs();
    }

    // Use shared candidates data if available
    if (sharedCandidates.length > 0) {
      setCandidates(sharedCandidates);

    } else {
      fetchCandidates();
    }

    hasInitialized.current = true;
  }, [sharedJobs.length, sharedCandidates.length, fetchJobs, fetchCandidates]);

  const getDepartmentName = (department: string | { _id?: string; name?: string } | any) => {
    try {
      // Handle null/undefined cases
      if (!department) {
        return 'General';
      }

      // If department is already an object with a name, return the name
      if (typeof department === 'object' && department?.name) {
        return department.name;
      }

      // If department is a string (ID), find the department by ID
      if (typeof department === 'string') {
        const foundDept = departments.find(dept => dept._id === department || dept.name === department);
        if (foundDept?.name) {
          return foundDept.name;
        }
        // If it's a string but not found in departments, return it as is (might be a department name)
        return department;
      }

      // Fallback
      return department?.toString() || 'General';
    } catch (error) {
      console.error('Error getting department name:', error);
      return 'General';
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDepartmentFilter('all');
    setTypeFilter('all');
    setLocationFilter('all');
    setDateRangeFilter('all');
    toast.success('Filters cleared');
  };

  // Helper function to show confirmation dialog
  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: {
      confirmText?: string;
      cancelText?: string;
      type?: 'danger' | 'warning' | 'info';
    }
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      type: options?.type || 'info'
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const handleConfirmAction = () => {
    confirmDialog.onConfirm();
    closeConfirmDialog();
  };

  const handleUpdateStatus = async (newStatus: any) => {
    if (!selectedJob) return;

    // Get the status name for comparison
    const statusName = typeof newStatus === 'string'
      ? newStatus
      : newStatus.label || newStatus.name || (newStatus.value ? formatStatus(newStatus.value) : formatStatus(newStatus));

    // Check if this is a close status
    const isCloseStatus = statusName.toLowerCase().includes('closed') ||
      statusName.toLowerCase().includes('terminated') ||
      statusName.toLowerCase().includes('ended') ||
      statusName.toLowerCase().includes('inactive');

    if (isCloseStatus) {
      // Show confirmation dialog for close status
      showConfirmDialog(
        'Close Job Posting',
        `Are you sure you want to close "${selectedJob.title}"?\n\nThis action will close the job posting and prevent new applications. You can reopen it later if needed.`,
        () => performStatusUpdate(newStatus),
        {
          confirmText: 'Close Job',
          cancelText: 'Cancel',
          type: 'danger'
        }
      );
    } else {
      // Show regular confirmation dialog for status update
      showConfirmDialog(
        'Update Job Status',
        `Are you sure you want to update "${selectedJob.title}" status to "${statusName}"?`,
        () => performStatusUpdate(newStatus),
        {
          confirmText: 'Update Status',
          cancelText: 'Cancel',
          type: 'info'
        }
      );
    }
  };

  const performStatusUpdate = async (newStatus: any) => {
    if (!selectedJob) return;

    // Send the status value (string) to the backend
    const statusValue = typeof newStatus === 'string'
      ? newStatus
      : newStatus.value || newStatus.name || newStatus._id || newStatus.id || newStatus;
    const statusName = typeof newStatus === 'string'
      ? formatStatus(newStatus)
      : newStatus.label || newStatus.name || formatStatus(newStatus);

    try {
      await updateJobStatus(getJobId(selectedJob), statusValue);
      // Refresh jobs and selected job
      const jobsResponse = await getJobPostings();
      setJobs(jobsResponse.data || []);
      const updatedJob = jobsResponse.data.find((job: JobPosting) => getJobId(job) === getJobId(selectedJob));
      setSelectedJob(updatedJob || null);
      toast.success("Status Updated", {
        description: `Status updated to ${statusName} for ${selectedJob.title}`,
      });
    } catch (error) {
      // Handled globally
    }
  };

  const handleSaveProfile = async () => {
    if (pendingStatus && selectedJob) {
      // Get the status name for comparison
      const statusName = formatStatus(pendingStatus);

      // Check if this is a close status
      const isCloseStatus = statusName.toLowerCase().includes('closed') ||
        statusName.toLowerCase().includes('terminated') ||
        statusName.toLowerCase().includes('ended') ||
        statusName.toLowerCase().includes('inactive');

      if (isCloseStatus) {
        // Show confirmation dialog for close status
        showConfirmDialog(
          'Close Job Posting',
          `Are you sure you want to close "${selectedJob.title}"?\n\nThis action will permanently close the job posting and prevent new applications. This action cannot be undone.`,
          () => performSaveProfileUpdate(),
          {
            confirmText: 'Close Job',
            cancelText: 'Cancel',
            type: 'danger'
          }
        );
      } else {
        // Show regular confirmation dialog for status update
        showConfirmDialog(
          'Update Job Status',
          `Are you sure you want to update "${selectedJob.title}" status to "${statusName}"?`,
          () => performSaveProfileUpdate(),
          {
            confirmText: 'Update Status',
            cancelText: 'Cancel',
            type: 'info'
          }
        );
      }
    }
  };

  const performSaveProfileUpdate = async () => {
    if (pendingStatus && selectedJob) {
      try {
        await updateJobStatus(getJobId(selectedJob), pendingStatus as JobPosting['status']);

        // Update local state
        setJobs(prevJobs =>
          prevJobs.map(job =>
            getJobId(job) === getJobId(selectedJob)
              ? ({ ...job, status: pendingStatus as JobPosting['status'] } as JobPosting)
              : job
          )
        );

        setSelectedJob(prev => prev ? {
          ...prev,
          status: pendingStatus as JobPosting['status']
        } : null);

        toast.success("Status Updated", {
          description: `Status updated to ${formatStatus(pendingStatus)} for ${selectedJob.title}`,
        });
        setPendingStatus(null);
        setIsProfileDirty(false);
      } catch (error) {
        console.error('Error updating job status:', error);
        // Handled globally
      }
    }
  };

  const handleAddJob = async (newJob: JobPosting) => {
    try {
      // Extract department ID from department (string or object)
      let departmentId: string;
      if (typeof newJob.department === 'object' && newJob.department?._id) {
        departmentId = newJob.department._id;
      } else if (typeof newJob.department === 'string') {
        // Check if it's a department name, find the ID
        const foundDept = departments.find(dept => dept.name === newJob.department);
        departmentId = foundDept?._id || newJob.department;
      } else {
        departmentId = newJob.department?.toString() || '';
      }

      const jobData = {
        title: newJob.title,
        department: departmentId,
        location: newJob.location,
        salaryRangeFrom: newJob.salaryRangeFrom,
        salaryRangeTo: newJob.salaryRangeTo,
        description: newJob.description,
        jobType: newJob.jobType,
        status: typeof newJob.status === 'string'
          ? newJob.status
          : (newJob.status as any)?.value || (newJob.status as any)?.name || 'OPEN',
        openPositions: newJob.openPositions
      };

      const createdJob = await createJobPosting(jobData);

      // Add timeline entry for the initial status if job was created successfully
      if (createdJob && createdJob.data && createdJob.data._id) {
        try {
          // Get the status name for the timeline entry
          // Temporarily disabled timeline creation until backend endpoints are ready
          // await timelineService.addJobTimelineEntry(createdJob.data._id, timelineEntry);

        } catch (timelineError) {
          console.warn('Failed to add initial timeline entry for job creation:', timelineError);
        }
      }

      // Refresh the list from server to ensure complete/clean data
      await fetchJobs();
      toast.success("Job Added Successfully", {
        description: `New job posting "${newJob.title}" has been created with status "${formatStatus(newJob.status)}"`,
      });
    } catch (error) {
      console.error('Error creating job:', error);
      // Handled globally
    }
  };

  const handleEditJob = (job: JobPosting) => {
    setJobToEdit({ ...job, _id: getJobId(job) });
    setIsEditDialogOpen(true);
  };

  const handleUpdateJob = async (updatedJob: JobPosting) => {
    try {
      if (!getJobId(updatedJob)) return;

      // Extract department ID from department (string or object)
      let departmentId: string;
      if (typeof updatedJob.department === 'object' && updatedJob.department?._id) {
        departmentId = updatedJob.department._id;
      } else if (typeof updatedJob.department === 'string') {
        // Check if it's a department name, find the ID
        const foundDept = departments.find(dept => dept.name === updatedJob.department);
        departmentId = foundDept?._id || updatedJob.department;
      } else {
        departmentId = updatedJob.department?.toString() || '';
      }

      const jobData = {
        title: updatedJob.title,
        department: departmentId,
        location: updatedJob.location,
        salaryRangeFrom: updatedJob.salaryRangeFrom,
        salaryRangeTo: updatedJob.salaryRangeTo,
        description: updatedJob.description,
        jobType: updatedJob.jobType,
        status: typeof updatedJob.status === 'string'
          ? updatedJob.status
          : (updatedJob.status as any)?.value || (updatedJob.status as any)?.name || 'OPEN',
        openPositions: updatedJob.openPositions
      };



      await updateJobPosting(getJobId(updatedJob), jobData);

      // Update local state
      setJobs(prevJobs =>
        prevJobs.map(job =>
          getJobId(job) === getJobId(updatedJob)
            ? {
              ...updatedJob,
            }
            : job
        )
      );

      // Update selected job if it's the same job being edited
      if (selectedJob && getJobId(selectedJob) === getJobId(updatedJob)) {
        setSelectedJob({
          ...selectedJob,
          ...updatedJob,
          department: updatedJob.department
        });
      }

      toast.success("Job Updated Successfully", {
        description: "Job posting has been updated",
      });
      setIsEditDialogOpen(false);
      setJobToEdit(null);
    } catch (error) {
      console.error('Error updating job:', error);
      // Handled globally
    }
  };

  const handleDeleteJob = async (job: JobPosting) => {
    showConfirmDialog(
      'Delete Job Posting',
      `Are you sure you want to delete "${job.title}"?\n\nThis action cannot be undone and will permanently remove the job posting and all associated data.`,
      async () => {
        try {
          const jobId = getJobId(job);
          if (jobId) {
            await deleteJobPosting(jobId);

            // Remove from local state
            setJobs(prevJobs => prevJobs.filter(j => getJobId(j) !== jobId));

            toast.success("Job Deleted Successfully", {
              description: `Job posting "${job.title}" has been deleted`,
            });

            // Close any open dialogs
            if (getJobId(selectedJob) === jobId) {
              setSelectedJob(null);
            }
          }
        } catch (error) {
          console.error('Error deleting job:', error);
          // Handled globally
        }
      },
      {
        confirmText: 'Delete Job',
        cancelText: 'Cancel',
        type: 'danger'
      }
    );
  };

  // Update formatStatus to handle both string and object
  const formatStatus = (status: any) => {
    if (!status) return 'Open';

    let statusStr = '';
    if (typeof status === 'object' && status !== null) {
      statusStr = status.value || status.name || status.label || '';
    } else {
      statusStr = status;
    }

    if (!statusStr || typeof statusStr !== 'string') return 'Open';

    // Find in JOB_STATUSES
    const statusObj = JOB_STATUSES.find(
      s => s.value === statusStr || s.label.toUpperCase() === statusStr.toUpperCase()
    );

    return statusObj ? statusObj.label : statusStr;
  };

  const filteredJobs = React.useMemo(() => {
    return jobs.filter(job => {
      // Skip invalid job objects
      if (!job || typeof job !== 'object') {
        return false;
      }

      const departmentName = getDepartmentName(job.department);
      const searchMatch =
        (job.title || '').toLowerCase().includes((searchQuery || '').toLowerCase()) ||
        (departmentName || '').toLowerCase().includes((searchQuery || '').toLowerCase());

      // Get the status name for comparison
      const jobStatusName = formatStatus(job.status);
      const statusMatch = statusFilter === 'all' || jobStatusName === statusFilter;



      // Handle department matching for both string IDs and objects
      let departmentMatch = departmentFilter === 'all';
      if (!departmentMatch) {
        if (typeof job.department === 'object' && job.department?._id) {
          departmentMatch = job.department._id === departmentFilter || departmentName === departmentFilter;
        } else if (typeof job.department === 'string') {
          departmentMatch = job.department === departmentFilter || departmentName === departmentFilter;
        }
      }

      const typeMatch = typeFilter === 'all' || job.jobType === typeFilter;
      const locationMatch = locationFilter === 'all' || (job.location || '').toLowerCase().includes(locationFilter.toLowerCase());

      const postedDate = new Date(job.postedDate || job.createdAt || '');
      const today = new Date();
      const dateMatch = dateRangeFilter === 'all' ||
        (dateRangeFilter === 'today' && postedDate.toDateString() === today.toDateString()) ||
        (dateRangeFilter === 'week' && (today.getTime() - postedDate.getTime()) <= 7 * 24 * 60 * 60 * 1000) ||
        (dateRangeFilter === 'month' && (today.getTime() - postedDate.getTime()) <= 30 * 24 * 60 * 60 * 1000);

      return searchMatch && statusMatch && departmentMatch && typeMatch && locationMatch && dateMatch;
    });
  }, [jobs, searchQuery, statusFilter, departmentFilter, typeFilter, locationFilter, dateRangeFilter, departments]);

  const sortedJobs = React.useMemo(() => {
    return [...filteredJobs].sort((a, b) => {
      if (sortConfig.key === 'title') {
        return sortConfig.direction === 'asc'
          ? (a.title || '').localeCompare(b.title || '')
          : (b.title || '').localeCompare(a.title || '');
      }
      if (sortConfig.key === 'postedDate') {
        const dateA = new Date(a.postedDate || a.createdAt || '');
        const dateB = new Date(b.postedDate || b.createdAt || '');
        return sortConfig.direction === 'asc'
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }
      return 0;
    });
  }, [filteredJobs, sortConfig]);

  const displayedJobs = limit ? sortedJobs.slice(0, limit) : sortedJobs;

  const handleViewTimeline = (job: JobPosting, eventId?: string, singleEvent: boolean = false) => {
    setSelectedTimelineJob(job);
    setSelectedTimelineEventId(eventId || null);
    setSingleEventOnly(singleEvent);
    setIsTimelineDialogOpen(true);
  };

  const handleViewJob = (job: any) => {
    setSelectedJob(job);
  };



  const formatJobType = (jobType: string) => {
    if (!jobType) return 'Full Time';
    const type = JOB_TYPES.find(t => t.value.toUpperCase() === jobType.toUpperCase());
    return type ? type.label : jobType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTimelineLabel = (event: TimelineEntry) => {
    if (event.type === 'status_change' || event.subType === 'status_change') {
      const newStatus = (event as any).metadata?.newStatus || event.subType;
      return formatStatus(newStatus as string);
    }
    const lower = (event.title || '').toLowerCase();
    if (lower.includes('closed')) return 'Closed';
    if (lower.includes('created')) return 'Created';
    return event.title || event.type;
  };

  // Helper function to get status color
  const getStatusColor = (status: any) => {
    // If status is an object with color property, use it
    if (typeof status === 'object' && status !== null && 'color' in status && typeof status.color === 'string') {
      return status.color;
    }

    // If status is an object with name, find the status object
    let statusStr = status;
    if (typeof status === 'object' && status !== null && 'name' in status) {
      statusStr = status.name;
    } else {
      statusStr = status;
    }

    // Default colors based on status name
    const statusName = typeof statusStr === 'string' ? statusStr.toUpperCase() : '';
    if (statusName === JobStatus.OPEN) return '#10b981'; // green
    if (statusName === JobStatus.CLOSED) return '#ef4444'; // red

    // Default color
    return '#6b7280'; // gray
  };

  // Helper function to get light color for background
  const getStatusLightColor = (status: any) => {
    // If status is an object with lightColor property, use it
    if (typeof status === 'object' && status !== null && 'lightColor' in status && typeof status.lightColor === 'string') {
      return status.lightColor;
    }

    // If status is an object with name, find the status object
    let statusStr = status;
    if (typeof status === 'object' && status !== null && 'name' in status) {
      statusStr = status.name;
    } else {
      statusStr = status;
    }

    const statusName = typeof statusStr === 'string' ? statusStr.toUpperCase() : '';
    if (statusName === JobStatus.OPEN) return '#ecfdf5'; // light green
    if (statusName === JobStatus.CLOSED) return '#fef2f2'; // light red

    return '#f3f4f6';
  };

  // Helper function to get actual color for border/text
  const getStatusActualColor = (status: any) => {
    // If status is an object with actualColor property, use it
    if (typeof status === 'object' && status !== null && 'actualColor' in status && typeof status.actualColor === 'string') {
      return status.actualColor;
    }

    // If status is an object with name, find the status object
    let statusStr = status;
    if (typeof status === 'object' && status !== null && 'name' in status) {
      statusStr = status.name;
    }

    // Status handling using predefined logic below

    // Default actual colors based on status name
    const statusName = typeof statusStr === 'string' ? statusStr.toUpperCase() : '';
    if (statusName === JobStatus.OPEN) return '#059669'; // dark green
    if (statusName === JobStatus.CLOSED) return '#dc2626'; // dark red

    // Default actual color
    return '#374151'; // dark gray
  };



  // 3. Fetch all candidates on mount
  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);
  // 4. Compute applicant counts per job
  useEffect(() => {

    const counts: Record<string, number> = {};
    candidates.forEach(candidate => {
      // Use jobPosting if present, else position._id if position is object, else position if string
      const jobId =
        candidate.jobPosting ||
        (candidate.position && typeof candidate.position === 'object' && candidate.position._id) ||
        (typeof candidate.position === 'string' && candidate.position);
      if (jobId) {
        counts[jobId] = (counts[jobId] || 0) + 1;
      }
    });
    setApplicantCounts(counts);
  }, [candidates]);


  return (
    <div className="space-y-4 p-4 pt-1 pb-10">
      {showFilters && (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-3xl font-bold tracking-tight">Jobs</h2>
            <div className="flex items-center gap-2">

              {canPostJobs && <AddJobDialog onAddJob={handleAddJob} />}
            </div>
          </div>

          <Card className="border border-gray-200 shadow-none rounded-xl mb-4 bg-white">
            <CardContent className="p-4">
              <div className="flex flex-wrap lg:flex-nowrap items-center gap-3 sm:gap-4 w-full">
                <div className="relative group flex-1 min-w-[200px]">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <Input
                    placeholder="Search jobs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-10 rounded-lg border-gray-200 focus:border-blue-500 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm bg-white"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1 h-8 w-8 hover:bg-gray-100 rounded-lg"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4 text-gray-400" />
                    </Button>
                  )}
                </div>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-10 w-full lg:w-[150px] rounded-lg border-gray-200 focus:border-blue-500 focus:ring-0 focus:ring-offset-0 text-sm bg-white shrink-0">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Filter className="h-4 w-4 text-gray-400 shrink-0" />
                      <SelectValue placeholder="Status" className="truncate" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="rounded-lg border-gray-200">
                    <SelectItem value="all">All Status</SelectItem>
                    {JOB_STATUSES.map(status => (
                      <SelectItem key={status.value} value={status.label}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="h-10 w-full lg:w-[180px] rounded-lg border-gray-200 focus:border-blue-500 focus:ring-0 focus:ring-offset-0 text-sm bg-white shrink-0">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                      <SelectValue placeholder="Department" className="truncate" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="rounded-lg border-gray-200">
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept._id || dept.name} value={dept._id || dept.name}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-10 w-full lg:w-[150px] rounded-lg border-gray-200 focus:border-blue-500 focus:ring-0 focus:ring-offset-0 text-sm bg-white shrink-0">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Briefcase className="h-4 w-4 text-gray-400 shrink-0" />
                      <SelectValue placeholder="Job Type" className="truncate" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="rounded-lg border-gray-200">
                    <SelectItem value="all">All Types</SelectItem>
                    {JOB_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger className="h-10 w-full lg:w-[160px] rounded-lg border-gray-200 focus:border-blue-500 focus:ring-0 focus:ring-offset-0 text-sm bg-white shrink-0">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
                      <SelectValue placeholder="Location" className="truncate" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="rounded-lg border-gray-200">
                    <SelectItem value="all">All Locations</SelectItem>
                    <SelectItem value="Ahmedabad">Ahmedabad</SelectItem>
                    <SelectItem value="Surat">Surat</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                  onClick={clearFilters}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {loading && displayedJobs.length === 0 ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading Jobs...</h2>
            <p className="text-gray-600">Please wait while we fetch your job postings.</p>
          </div>
        </div>
      ) : displayedJobs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {jobs.length === 0
              ? "No job postings found"
              : `No job postings match your current filters (${jobs.length} total jobs available)`
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ">
          {displayedJobs.map((job) => (
            <Card
              key={getJobId(job)}
              className={`hover:shadow-md transition-shadow h-[380px] flex flex-col relative border-l-4 ${formatStatus(job.status).toLowerCase().includes('closed') ||
                formatStatus(job.status).toLowerCase().includes('terminated')
                ? 'opacity-75 bg-gray-50'
                : ''
                }`}
              style={{ borderLeftColor: getStatusColor(job.status) }}
            >
              <CardHeader className="pb-3 flex-shrink-0">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-lg leading-tight mb-1">{job.title || 'Untitled Job'}</CardTitle>
                    <CardDescription className="text-sm text-gray-600">{getDepartmentName(job.department) || 'General'}</CardDescription>
                  </div>
                  <Badge
                    variant="outline"
                    style={{
                      backgroundColor: getStatusLightColor(job.status),
                      color: getStatusActualColor(job.status),
                    }}
                    className="border-current text-xs font-medium"
                  >
                    {formatStatus(job.status)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="py-2 flex-1 flex flex-col overflow-hidden">
                <div className="space-y-2 flex-shrink-0">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{job.openPositions || 1} open position{(job.openPositions || 1) > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Briefcase className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{formatJobType(job.jobType)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">Posted on {job.postedDate || new Date(job.createdAt || '').toISOString().split('T')[0]}</span>
                  </div>
                  <div
                    className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer hover:text-blue-600 transition-colors group"
                    onClick={() => navigate(`/hiring/candidates?jobId=${getJobId(job)}`)}
                    title="View applicants for this job"
                  >
                    <User className="h-4 w-4 text-gray-400 flex-shrink-0 group-hover:text-blue-500" />
                    <span className="truncate font-medium underline underline-offset-4 decoration-blue-200 group-hover:decoration-blue-400">
                      {applicantCounts[getJobId(job)] || 0} applicants
                    </span>
                  </div>
                  {(job.salaryRangeFrom || job.salaryRangeTo) && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <IndianRupee className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate">
                        ₹{job.salaryRangeFrom?.toLocaleString() || '0'} - ₹{job.salaryRangeTo?.toLocaleString() || '0'}
                      </span>
                    </div>
                  )}
                </div>

                {job.timeline && job.timeline.length > 0 && (
                  <div className="mt-2 pt-1 border-t border-gray-100 flex-1 flex flex-col justify-center min-h-0 overflow-auto">
                    {/* Prepare unique preview events */}
                    {(() => {
                      const uniqueEvents: typeof job.timeline = [];
                      const seenLabels = new Set<string>();
                      [...job.timeline!].reverse().forEach(ev => {
                        const label = getTimelineLabel(ev);
                        if (!seenLabels.has(label)) {
                          seenLabels.add(label);
                          uniqueEvents.push(ev);
                        }
                      });
                      const preview = uniqueEvents.slice(0, 4);

                      return (
                        <>
                          {/* Desktop timeline (horizontal) */}
                          <div className="hidden sm:flex items-center justify-between relative">
                            {preview.map((event, index) => (
                              <div key={event._id || index} className="flex flex-col items-center relative flex-1">
                                {index < preview.length - 1 && (
                                  <div
                                    className="absolute top-1.5 left-1/2 h-0.5 bg-gray-200 z-0"
                                    style={{ width: 'calc(100% + 12px)', transform: 'translateX(6px)' }}
                                  />
                                )}

                                <div
                                  className="w-3 h-3 sm:w-4 sm:h-4 rounded-full z-10 relative cursor-pointer hover:scale-110 transition-transform"
                                  style={{ backgroundColor: '#3b82f6' }}
                                  onClick={() => handleViewTimeline(job, event._id as string, true)}
                                  title={`${event.title} - ${new Date(event.createdAt).toLocaleDateString()}`}
                                />

                                <div className="mt-0.5 text-center">
                                  <p className="text-[10px] sm:text-xs font-medium text-gray-700 truncate max-w-16 sm:max-w-20">
                                    {getTimelineLabel(event)}
                                  </p>
                                  <p className="text-[8px] sm:text-[10px] text-gray-500 mt-0.5">
                                    {new Date(event.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Mobile timeline (vertical) */}
                          <div className="sm:hidden space-y-2 max-h-20 overflow-y-auto">
                            {preview.slice(0, 3).map((event, index) => (
                              <div
                                key={event._id || index}
                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                                onClick={() => handleViewTimeline(job, event._id as string, true)}
                              >
                                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#3b82f6' }} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-[9px] font-medium text-gray-700 truncate">
                                    {getTimelineLabel(event)}
                                  </p>
                                  <p className="text-[8px] text-gray-500">
                                    {new Date(event.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>

                          {job.timeline!.length > 4 && (
                            <div className="text-center mt-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[8px] sm:text-[10px] text-gray-500 hover:text-gray-700 h-4 sm:h-6 px-1 sm:px-2"
                                onClick={() => handleViewTimeline(job, job.timeline![job.timeline!.length - 1]._id as string, false)}
                              >
                                <span className="hidden sm:inline">View all {job.timeline!.length}</span>
                                <span className="sm:hidden">+{job.timeline!.length - 3}</span>
                              </Button>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex gap-2 pt-2 pb-3 px-3 flex-shrink-0 mt-auto border-t border-gray-50">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-8 text-xs px-2 py-1 rounded-full bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700 hover:text-blue-800"
                  onClick={() => handleViewJob(job)}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <JobTimelineDialog
        job={selectedTimelineJob}
        open={isTimelineDialogOpen}
        onOpenChange={setIsTimelineDialogOpen}
        initialEventId={selectedTimelineEventId}
        singleEventOnly={singleEventOnly}
      />

      <Dialog open={!!selectedJob} onOpenChange={() => {
        if (isProfileDirty) {
          if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
            setSelectedJob(null);
            setIsProfileDirty(false);
            setPendingStatus(null);
          }
        } else {
          setSelectedJob(null);
        }
      }}>
        <DialogContent className="sm:max-w-[900px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
          {/* Hidden accessible title/description */}
          <DialogHeader className="sr-only">
            <DialogTitle>Job Details</DialogTitle>
            <DialogDescription>Detailed view of the selected job posting</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {selectedJob && (
                <>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-lg">
                          <Briefcase className="h-8 w-8" />
                        </div>
                        <div>
                          <h1 className="text-2xl font-bold text-gray-900 mb-1">
                            {selectedJob.title || 'Untitled Job'}
                          </h1>
                          <p className="text-gray-600 text-base font-medium">
                            {getDepartmentName(selectedJob.department) || 'General'}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <Badge
                              variant="outline"
                              style={{
                                backgroundColor: getStatusLightColor(pendingStatus || selectedJob.status),
                                color: getStatusActualColor(pendingStatus || selectedJob.status),
                              }}
                              className="border-current text-xs font-medium"
                            >
                              {formatStatus(pendingStatus || selectedJob.status)}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              Posted on {selectedJob.postedDate || new Date(selectedJob.createdAt || '').toISOString().split('T')[0]}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {canEditJobs && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200">
                                Update Status
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              {JOB_STATUSES.map((status: any) => {
                                // Get current status name for comparison
                                const currentStatusName = formatStatus(selectedJob.status);
                                const isCurrentStatus = currentStatusName === status.label;

                                return (
                                  <DropdownMenuItem
                                    key={status.value}
                                    disabled={isCurrentStatus}
                                    onClick={() => handleUpdateStatus(status)}
                                    className={isCurrentStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                  >
                                    Mark as {status.label}
                                    {isCurrentStatus && ' (Current)'}
                                  </DropdownMenuItem>
                                );
                              })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                        {(canEditJobs || canDeleteJobs) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="bg-gray-50 hover:bg-gray-100">
                                Actions
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              {canEditJobs && (
                                <DropdownMenuItem onClick={() => handleEditJob(selectedJob)} className="cursor-pointer">
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Job
                                </DropdownMenuItem>
                              )}
                              {canDeleteJobs && (
                                <DropdownMenuItem
                                  onClick={() => handleDeleteJob(selectedJob)}
                                  className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Job
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                        {canEditJobs && isProfileDirty && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={handleSaveProfile}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Save Changes
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-xl p-6 shadow-sm border border-blue-100">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <Briefcase className="h-5 w-5 text-blue-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Job Details</h2>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{selectedJob.openPositions || 1} position{(selectedJob.openPositions || 1) > 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{formatJobType(selectedJob.jobType)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{applicantCounts[getJobId(selectedJob)] || 0} applicants</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-xl p-6 shadow-sm border border-green-100">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                          <MapPin className="h-5 w-5 text-green-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Location</h2>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{selectedJob.location || 'Remote'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-orange-50 rounded-xl p-6 shadow-sm border border-orange-100">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                          <FileText className="h-5 w-5 text-orange-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Salary Range</h2>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <IndianRupee className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium">
                            ₹{selectedJob.salaryRangeFrom?.toLocaleString() || '0'} - ₹{selectedJob.salaryRangeTo?.toLocaleString()}
                          </span>
                        </div>
                        {selectedJob.salaryRangeFrom && selectedJob.salaryRangeTo && (
                          <div className="flex items-center gap-2">

                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 rounded-xl p-6 shadow-sm border border-yellow-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                        <FileText className="h-5 w-5 text-yellow-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Job Description</h2>
                    </div>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedJob.description || 'Job description will be updated soon.'}</p>
                  </div>

                  <div className="bg-purple-50 rounded-xl p-6 shadow-sm border border-purple-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Activity className="h-5 w-5 text-purple-600" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Job Timeline</h2>
                    </div>
                    <div className="space-y-4">
                      {selectedJob.timeline && selectedJob.timeline.length > 0 ? (
                        selectedJob.timeline.map((event: TimelineEntry, index: number) => (
                          <div key={event._id || index} className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex flex-col items-center">
                              <div
                                className="w-3 h-3 rounded-full border-2 border-white shadow-sm"
                                style={{ backgroundColor: '#3b82f6' }}
                              />
                              {index < (selectedJob.timeline?.length || 0) - 1 && (
                                <div className="w-0.5 h-8 bg-gray-200 mt-2" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <p className="font-semibold text-gray-900">
                                  {(() => {
                                    if (event.type === 'status_change' || event.subType === 'status_change') {
                                      const newStatus = event.metadata?.newStatus || event.subType;
                                      return formatStatus(newStatus as string);
                                    }
                                    const lower = (event.title || '').toLowerCase();
                                    if (lower.includes('closed')) return 'Closed';
                                    if (lower.includes('created')) return 'Created';
                                    return event.title || event.type;
                                  })()}
                                </p>
                                <div className="flex gap-2">
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                    {new Date(event.createdAt).toLocaleDateString()}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                    {new Date(event.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </Badge>
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{event.description}</p>
                              {event.createdBy && (
                                <p className="text-xs text-gray-500">
                                  by {event.createdBy.firstName} {event.createdBy.lastName}
                                </p>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                          <p className="text-sm text-gray-500">No timeline events available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AddJobDialog
        onAddJob={handleUpdateJob}
        initialData={jobToEdit || undefined}
        isEdit={true}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={closeConfirmDialog}>
        <DialogContent className="sm:max-w-[440px] border border-gray-100 shadow-2xl p-0 overflow-hidden rounded-2xl bg-white">
          <DialogTitle className="sr-only">{confirmDialog.title}</DialogTitle>
          <DialogDescription className="sr-only">{confirmDialog.message}</DialogDescription>

          <div className="p-6">
            <div className="flex items-start gap-4 pt-2">
              {/* ── Minimal Professional Icon ── */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm border ${confirmDialog.type === 'danger' ? 'bg-red-50 border-red-100' :
                confirmDialog.type === 'warning' ? 'bg-amber-50 border-amber-100' :
                  'bg-blue-50 border-blue-100'
                }`}>
                {confirmDialog.type === 'danger' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                {confirmDialog.type === 'warning' && <AlertTriangle className="h-5 w-5 text-amber-600" />}
                {confirmDialog.type === 'info' && <Info className="h-5 w-5 text-blue-600" />}
              </div>

              {/* ── Content ── */}
              <div className="flex-1 min-w-0 pr-4">
                <h3 className="text-[17px] font-bold text-gray-900 tracking-tight leading-tight mb-2">
                  {confirmDialog.title}
                </h3>
                <p className="text-[13.5px] text-gray-500 leading-relaxed font-medium">
                  {confirmDialog.message}
                </p>
              </div>
            </div>

            {/* ── Professional Actions ── */}
            <div className="flex items-center justify-end gap-2.5 mt-8 pt-2">
              <Button
                variant="ghost"
                onClick={closeConfirmDialog}
                className="h-9 px-4 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 text-[11px] font-bold uppercase tracking-wider transition-all"
              >
                {confirmDialog.cancelText}
              </Button>
              <Button
                variant={confirmDialog.type === 'danger' ? 'destructive' : 'default'}
                onClick={handleConfirmAction}
                className={`h-9 px-5 rounded-lg text-[11px] font-bold uppercase tracking-widest transition-all shadow-sm ${confirmDialog.type === 'danger' ? 'bg-red-500 hover:bg-red-600' :
                  confirmDialog.type === 'warning' ? 'bg-amber-500 hover:bg-amber-600' :
                    'bg-indigo-600 hover:bg-indigo-700'
                  }`}
              >
                {confirmDialog.confirmText}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobsList; 